import { icon, node } from "~/util/dom";
import { ERROR, IMG_SIZE } from "~/report/const";
import { ICommonOpts } from "~/report/interfaces";

interface IOpts extends ICommonOpts {
	id?: number;
	file: File;
	onRemove: (event: ReportFormImage) => void;
	onDomChange: () => void;
	minWidth?: number;
	minHeight?: number;
	upload: (scope: ReportFormImage) => Promise<any>;
	onUpload?: (scope: ReportFormImage, data: any) => void;
	mimeTypes?: Array<string>;
}

export default class ReportFormImage {
	#opts: IOpts;

	#container: HTMLElement;

	#imageURL: string;

	#id: number;

	#overlay: HTMLElement;

	#deleteButton: HTMLButtonElement;

	#settingFile: any;

	constructor(opts: IOpts) {
		const { id } = opts;

		this.#opts = {
			id: 0,
			file: null,
			onRemove: () => { /**/ },
			parent: null,
			onDomChange: () => { /**/ },
			minWidth: 800,
			minHeight: 600,
			upload: () => Promise.resolve({}),
			onUpload: null,
			mimeTypes: Mapy.ImageUploader.MIME_TYPES,
			...opts,
		};

		if (id) {
			this.#id = id;
		}
		this.#settingFile = this.#setFile();
	}

	set id(value: number) {
		this.#id = value;
	}

	get id() {
		return this.#id;
	}

	get settingFile() {
		return this.#settingFile;
	}

	get deleteButton() {
		return this.#deleteButton;
	}

	get file() {
		return this.#opts.file;
	}

	#revoke() {
		if (this.#imageURL) {
			URL.revokeObjectURL(this.#imageURL);
			this.#imageURL = "";
		}
	}

	remove() {
		this.#opts.onRemove(this);
		this.#opts.parent.removeChild(this.#container);
		this.#opts.onDomChange();
	}

	#onRemove(event: any) {
		event.stopPropagation();
		event.preventDefault();
		this.remove();
	}

	#build(image: HTMLCanvasElement) {
		const { parent } = this.#opts;

		this.#container = node("div", { className: "image-item" });
		this.#overlay = node("div", { className: "image-overlay" });
		this.#deleteButton = node("button", { type: "button", className: "image-delete-btn" });

		this.#deleteButton.append(icon("delete"));
		this.#deleteButton.addEventListener("click", this.#onRemove.bind(this));

		this.#container.append(image, this.#overlay, this.#deleteButton);

		parent.append(this.#container);
		this.#opts.onDomChange();
	}

	uploadProgress(percent) {
		this.#overlay.style.left = `${percent}%`;
	}

	#onLoad(image: HTMLImageElement, resolve: (data: any) => void, reject: (error: number) => void) {
		return () => {
			const width = image.naturalWidth;
			const height = image.naturalHeight;

			const { minWidth, minHeight } = this.#opts;

			if (width < minWidth || height < minHeight) {
				// uvolnime pamet
				this.#revoke();
				// vrátíme chybu
				reject(ERROR.SIZE);
			} else {
				const canvas = node("canvas", { width: IMG_SIZE, height: IMG_SIZE });
				const context = canvas.getContext("2d");

				let canWidth = 0;
				let canHeight = 0;

				if (width > height) {
					canHeight = IMG_SIZE;
					canWidth = Math.floor(width / height * IMG_SIZE);
				} else {
					canWidth = IMG_SIZE;
					canHeight = Math.floor(height / width * IMG_SIZE);
				}

				canvas.width = canWidth;
				canvas.height = canHeight;
				context.drawImage(image, 0, 0, width, height, 0, 0, canWidth, canHeight);

				// uvolnime pamet
				this.#revoke();
				// vytvoříme obrázek
				this.#build(canvas);

				this.#opts.upload(this)
					.then(data => {
						if (typeof this.#opts.onUpload === "function") {
							this.#opts.onUpload(this, data);
						}
						resolve(data);
					})
					.catch(() => {
						this.remove();
						reject(ERROR.UPLOAD);
					});
			}
		};
	}

	#setFile() {
		return new Promise((resolve, reject) => {
			const { file, mimeTypes } = this.#opts;

			if (!mimeTypes.includes(file.type)) {
				reject(ERROR.EXT);

				return;
			}

			try {
				this.#imageURL = URL.createObjectURL(file);
				const image = new Image();

				image.addEventListener("load", this.#onLoad(image, resolve, reject));
				image.addEventListener("error", () => reject(ERROR.ERROR));
				image.src = this.#imageURL;
				image.crossOrigin = "anonymous";
			} catch (exc) {
				reject(ERROR.ERROR);
			}
		});
	}
}
